import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// Import icons for header buttons
import { Show, TickSquare } from 'react-iconly';
import { COLORS } from '../lib/styleGuide';
import { useQuickActions } from '../contexts/QuickActionsContext';
import NotificationBell from './NotificationBell';

const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};

const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

export function SellerHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [isNavDropdownOpen, setIsNavDropdownOpen] = useState(false);
  const { isQuickActionsOpen, toggleQuickActions } = useQuickActions();

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', path: '/seller-hub' },
    { id: 'products', label: 'Products', path: '/all-products' },
    { id: 'orders', label: 'Orders', path: '/orders' },
    { id: 'messages', label: 'Messages', path: '/seller-hub/messages' },
    { id: 'settings', label: 'Settings', path: `/store-settings/${currentUser?.uid}` }
  ];

  const currentItem = navItems.find(item => item.path === location.pathname) || navItems[0];

  return (
    <header style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 100,
      background: 'rgba(255,255,255,0.95)',
      backdropFilter: 'blur(10px)',
      borderBottom: '1px solid rgba(0,0,0,0.1)',
      padding: '16px 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      {/* Left side - Title and Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={toggleQuickActions}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = NEUTRAL_BG;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <div style={{
              width: 16,
              height: 12,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}>
              <div style={{
                width: '100%',
                height: 2,
                background: NEUTRAL_TEXT,
                borderRadius: 1,
                transition: 'all 0.2s ease'
              }} />
              <div style={{
                width: '100%',
                height: 2,
                background: NEUTRAL_TEXT,
                borderRadius: 1,
                transition: 'all 0.2s ease'
              }} />
              <div style={{
                width: '100%',
                height: 2,
                background: NEUTRAL_TEXT,
                borderRadius: 1,
                transition: 'all 0.2s ease'
              }} />
            </div>
          </button>
          <h1 style={{
            fontSize: 16,
            fontWeight: 700,
            color: NEUTRAL_TEXT,
            margin: 0,
            ...headingFont
          }}>
            Seller Hub
          </h1>
        </div>

        {/* Desktop Navigation */}
        {window.innerWidth > 768 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}>
            {navItems.map(navItem => (
              <button
                key={navItem.id}
                onClick={() => navigate(navItem.path)}
                                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '8px 16px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: navItem.path === location.pathname ? PRIMARY_BROWN : ACCENT_GRAY,
                    cursor: 'pointer',
                    borderRadius: 20,
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                    ...bodyFont
                  }}
                onMouseOver={(e) => {
                  if (navItem.path !== location.pathname) {
                    e.currentTarget.style.background = NEUTRAL_BG;
                    e.currentTarget.style.color = NEUTRAL_TEXT;
                  }
                }}
                onMouseOut={(e) => {
                  if (navItem.path !== location.pathname) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = ACCENT_GRAY;
                  }
                }}
              >
                {navItem.label}
              </button>
            ))}
          </div>
        )}

        {/* Mobile Navigation Dropdown */}
        {window.innerWidth <= 768 && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setIsNavDropdownOpen(!isNavDropdownOpen)}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '8px 0',
                fontSize: 12,
                fontWeight: 600,
                color: PRIMARY_BROWN,
                cursor: 'pointer',
                textAlign: 'left',
                ...bodyFont
              }}
            >
              {currentItem.label}
            </button>
            
            {isNavDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: NEUTRAL_WHITE,
                border: `1px solid ${NEUTRAL_BG}`,
                borderRadius: 8,
                marginTop: 4,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
                overflow: 'visible',
                minWidth: '120px'
              }}>
                {navItems.map(navItem => (
                  <button
                    key={navItem.id}
                    onClick={() => {
                      navigate(navItem.path);
                      setIsNavDropdownOpen(false);
                    }}
                    style={{
                      width: '100%',
                      background: 'transparent',
                      color: navItem.path === location.pathname ? PRIMARY_BROWN : NEUTRAL_TEXT,
                      border: 'none',
                      padding: '12px 16px',
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: 'pointer',
                      textAlign: 'left',
                      borderBottom: `1px solid ${NEUTRAL_BG}`,
                      transition: 'all 0.2s ease',
                      ...bodyFont
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = NEUTRAL_BG;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {navItem.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right side - Action Buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          style={{
            background: 'rgba(255,255,255,0.9)',
            border: 'none',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'relative'
          }}
          onClick={() => navigate(`/store-preview/${currentUser?.uid}`)}
          aria-label="Go to Store Preview"
        >
          <Show set="regular" size={20} primaryColor={NEUTRAL_TEXT} />
        </button>
        <NotificationBell />
      </div>
    </header>
  );
}
